import { getDocument, GlobalWorkerOptions, type TextItem } from 'pdfjs-dist'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { createWorker } from 'tesseract.js'

import { callClaude } from './claude-client'
import { extractDateFromFileName, normalizeOptionalMedicalDate, resolveAnalysisDates } from './medical-document-dates'
import type { DocumentAnalysisResult, DocumentType } from '@/types/health'

GlobalWorkerOptions.workerSrc = pdfWorkerUrl

const MAX_PDF_PAGES = 20
const MAX_OCR_PAGES = 8
const MAX_TEXT_LENGTH = 40000
const MIN_EMBEDDED_TEXT_LENGTH = 180
const OCR_RENDER_SCALE = 2
const OCR_LANGUAGE = 'fra+eng'

const DOCUMENT_TYPES: DocumentType[] = [
  'blood_test',
  'radiology',
  'prescription',
  'consultation_report',
  'discharge_summary',
  'pathology',
  'cardiology',
  'specialist_report',
  'vaccination_record',
  'insurance_document',
  'other'
]

const ANALYSIS_STATUSES = ['normal', 'anormal', 'limite', 'critique'] as const
const DIAGNOSTIC_TYPES = ['principal', 'secondaire', 'suspect', 'elimine'] as const
const SEVERITY_TYPES = ['leger', 'modere', 'severe', 'critique'] as const

type OcrWorker = Awaited<ReturnType<typeof createWorker>>

let ocrWorkerPromise: Promise<OcrWorker> | null = null
let ocrQueue: Promise<void> = Promise.resolve()

function sanitizeClaudeJsonResponse(rawResponse: string): string {
  const trimmed = rawResponse.trim()

  if (trimmed.startsWith('```')) {
    return trimmed
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/\s*```$/, '')
      .trim()
  }

  const firstBrace = trimmed.indexOf('{')
  const lastBrace = trimmed.lastIndexOf('}')

  if (firstBrace >= 0 && lastBrace > firstBrace) {
    return trimmed.slice(firstBrace, lastBrace + 1)
  }

  return trimmed
}

function normalizeDocumentType(value: unknown, fileName: string): DocumentType {
  if (typeof value === 'string' && DOCUMENT_TYPES.includes(value as DocumentType)) {
    return value as DocumentType
  }

  const lowerName = fileName.toLowerCase()
  if (lowerName.includes('ordonnance') || lowerName.includes('prescription')) return 'prescription'
  if (lowerName.includes('analyse') || lowerName.includes('bio') || lowerName.includes('labo')) return 'blood_test'
  if (lowerName.includes('scanner') || lowerName.includes('irm') || lowerName.includes('radio')) return 'radiology'
  if (lowerName.includes('consultation') || lowerName.includes('compte-rendu')) return 'consultation_report'

  return 'other'
}

function normalizeNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

type ExtractedAnalysisEntry = DocumentAnalysisResult['analyses'][number]

function normalizeAnalysisText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function isBloodTestLike(typeDocument: DocumentType, fileName: string): boolean {
  if (typeDocument === 'blood_test') {
    return true
  }

  const lowerName = fileName.toLowerCase()
  return /analyse|bio|labo|hemat|hemato|ferrit|crp|nfs/.test(lowerName)
}

function createAnalysisFingerprint(entry: ExtractedAnalysisEntry): string {
  return [
    normalizeAnalysisText(entry.nom_analyse),
    normalizeAnalysisText(entry.valeur || ''),
    normalizeAnalysisText(entry.unite || ''),
    normalizeAnalysisText(entry.valeur_reference || '')
  ].join('|')
}

function mergeExtractedAnalyses(
  primaryAnalyses: ExtractedAnalysisEntry[],
  supplementalAnalyses: ExtractedAnalysisEntry[]
): ExtractedAnalysisEntry[] {
  const merged = new Map<string, ExtractedAnalysisEntry>()

  for (const entry of [...primaryAnalyses, ...supplementalAnalyses]) {
    const fingerprint = createAnalysisFingerprint(entry)
    const existingEntry = merged.get(fingerprint)

    if (!existingEntry) {
      merged.set(fingerprint, entry)
      continue
    }

    merged.set(fingerprint, {
      nom_analyse: existingEntry.nom_analyse || entry.nom_analyse,
      valeur: existingEntry.valeur || entry.valeur,
      unite: existingEntry.unite || entry.unite,
      valeur_reference: existingEntry.valeur_reference || entry.valeur_reference,
      statut: existingEntry.statut || entry.statut
    })
  }

  return [...merged.values()]
}

function buildBloodTestVerificationPrompt(
  fileName: string,
  textContent: string,
  existingAnalyses: ExtractedAnalysisEntry[]
): string {
  return `
Tu verifies la COMPLETUDE d'un bilan biologique deja extrait.

Objectif:
- relire tout le texte du document
- conserver toutes les analyses deja trouvees
- ajouter les analyses biologiques manquantes
- ne jamais inventer une ligne absente
- conserver les noms d'analyse, valeurs, unites et references telles qu'elles apparaissent
- extraire aussi les lignes de NFS, formule leucocytaire, plaquettes, indices erythrocytaires, fer, ferritine, transferrine, vitamines, bilan inflammatoire, bilan hepatique, bilan renal et ionogramme si presentes

Retourne STRICTEMENT un JSON minifie de la forme:
{"analyses":[{"nom_analyse":"string","valeur":"string ou null","unite":"string ou null","valeur_reference":"string ou null","statut":"normal|anormal|limite|critique ou null"}]}

Liste deja extraite:
${JSON.stringify(existingAnalyses)}

Nom du fichier: ${fileName}

Texte du document:
${textContent || '[aucun texte extrait]'}
`.trim()
}

async function verifyBloodTestCompleteness(
  fileName: string,
  textContent: string,
  analysisResult: DocumentAnalysisResult
): Promise<DocumentAnalysisResult> {
  if (!isBloodTestLike(analysisResult.type_document, fileName) || !textContent.trim()) {
    return analysisResult
  }

  try {
    const response = await callClaude(
      buildBloodTestVerificationPrompt(fileName, textContent, analysisResult.analyses),
      'Tu es un assistant medical expert des bilans biologiques. Tu retournes uniquement un JSON valide et complet.',
      4000
    )

    const parsed = JSON.parse(sanitizeClaudeJsonResponse(response)) as { analyses?: unknown[] }
    const normalizedSupplement = normalizeAnalysisResult(
      {
        type_document: analysisResult.type_document,
        date_document: analysisResult.date_document,
        date_prelevement: analysisResult.date_prelevement,
        medecin: analysisResult.medecin,
        laboratoire: analysisResult.laboratoire,
        prescriptions: [],
        analyses: Array.isArray(parsed.analyses) ? parsed.analyses : [],
        diagnostics: [],
        recommandations: analysisResult.recommandations,
        resume: analysisResult.resume
      },
      fileName
    )

    return {
      ...analysisResult,
      analyses: mergeExtractedAnalyses(analysisResult.analyses, normalizedSupplement.analyses)
    }
  } catch (error) {
    console.warn('Verification de completude biologique indisponible:', error)
    return analysisResult
  }
}

function normalizeAnalysisResult(raw: unknown, fileName: string): DocumentAnalysisResult {
  const source = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}

  const prescriptionsSource = Array.isArray(source.prescriptions) ? source.prescriptions : []
  const analysesSource = Array.isArray(source.analyses) ? source.analyses : []
  const diagnosticsSource = Array.isArray(source.diagnostics) ? source.diagnostics : []

  const typeDocument = normalizeDocumentType(source.type_document, fileName)
  const resolvedDates = resolveAnalysisDates({
    fileName,
    typeDocument,
    dateDocument: source.date_document,
    datePrelevement: source.date_prelevement
  })

  return {
    type_document: typeDocument,
    date_document: resolvedDates.dateDocument,
    medecin: normalizeNullableString(source.medecin),
    laboratoire: normalizeNullableString(source.laboratoire),
    date_prelevement: resolvedDates.datePrelevement,
    resume: normalizeNullableString(source.resume) || `Analyse du document ${fileName}`,
    recommandations: normalizeNullableString(source.recommandations) || undefined,
    prescriptions: prescriptionsSource
      .map((entry) => {
        const item = entry as Record<string, unknown>
        const medicament = normalizeNullableString(item.medicament)

        if (!medicament) {
          return null
        }

        return {
          medicament,
          dosage: normalizeNullableString(item.dosage) || undefined,
          frequence: normalizeNullableString(item.frequence) || undefined,
          duree: normalizeNullableString(item.duree) || undefined,
          instructions: normalizeNullableString(item.instructions) || undefined
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    analyses: analysesSource
      .map((entry) => {
        const item = entry as Record<string, unknown>
        const nomAnalyse = normalizeNullableString(item.nom_analyse)

        if (!nomAnalyse) {
          return null
        }

        const statut = normalizeNullableString(item.statut)

        return {
          nom_analyse: nomAnalyse,
          valeur: normalizeNullableString(item.valeur) || undefined,
          unite: normalizeNullableString(item.unite) || undefined,
          valeur_reference: normalizeNullableString(item.valeur_reference) || undefined,
          statut: statut && ANALYSIS_STATUSES.includes(statut as (typeof ANALYSIS_STATUSES)[number])
            ? (statut as (typeof ANALYSIS_STATUSES)[number])
            : undefined
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    diagnostics: diagnosticsSource
      .map((entry) => {
        const item = entry as Record<string, unknown>
        const diagnostic = normalizeNullableString(item.diagnostic)

        if (!diagnostic) {
          return null
        }

        const typeDiagnostic = normalizeNullableString(item.type_diagnostic)
        const gravite = normalizeNullableString(item.gravite)

        return {
          diagnostic,
          type_diagnostic: typeDiagnostic && DIAGNOSTIC_TYPES.includes(typeDiagnostic as (typeof DIAGNOSTIC_TYPES)[number])
            ? (typeDiagnostic as (typeof DIAGNOSTIC_TYPES)[number])
            : 'principal',
          gravite: gravite && SEVERITY_TYPES.includes(gravite as (typeof SEVERITY_TYPES)[number])
            ? (gravite as (typeof SEVERITY_TYPES)[number])
            : undefined
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
  }
}

async function loadPdfDocument(fileUrl: string) {
  const response = await fetch(fileUrl)

  if (!response.ok) {
    throw new Error(`Impossible de telecharger le PDF: ${response.status}`)
  }

  const arrayBuffer = await response.arrayBuffer()
  const loadingTask = getDocument({ data: arrayBuffer })
  return loadingTask.promise
}

type PdfDocument = Awaited<ReturnType<typeof loadPdfDocument>>

function normalizePdfText(text: string): string {
  return text
    .replace(/\u0000/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[^\S\r\n]{2,}/g, ' ')
    .trim()
}

function shouldRunOcr(extractedText: string): boolean {
  const normalized = normalizePdfText(extractedText)
  const wordCount = normalized.split(/\s+/).filter(Boolean).length
  const lineCount = normalized.split('\n').filter((line) => line.trim().length > 0).length

  return normalized.length < MIN_EMBEDDED_TEXT_LENGTH || wordCount < 40 || lineCount < 3
}

async function extractEmbeddedTextFromPdf(pdfDocument: PdfDocument): Promise<string> {
  const pagesToRead = Math.min(pdfDocument.numPages, MAX_PDF_PAGES)
  const pageTexts: string[] = []

  for (let pageNumber = 1; pageNumber <= pagesToRead; pageNumber += 1) {
    const page = await pdfDocument.getPage(pageNumber)
    const textContent = await page.getTextContent()
    const items = textContent.items as TextItem[]

    const pageText = normalizePdfText(
      items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
    )

    if (pageText) {
      pageTexts.push(`Page ${pageNumber}: ${pageText}`)
    }
  }

  return pageTexts.join('\n\n').slice(0, MAX_TEXT_LENGTH)
}

async function getOcrWorker(): Promise<OcrWorker> {
  if (!ocrWorkerPromise) {
    ocrWorkerPromise = createWorker(OCR_LANGUAGE)
  }

  return ocrWorkerPromise
}

async function queueOcrTask<T>(task: () => Promise<T>): Promise<T> {
  const nextTask = ocrQueue.then(task, task)
  ocrQueue = nextTask.then(() => undefined, () => undefined)
  return nextTask
}

function prepareCanvasForOcr(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const context = canvas.getContext('2d', { willReadFrequently: true })

  if (!context) {
    return canvas
  }

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let index = 0; index < data.length; index += 4) {
    const grayscale = (0.299 * data[index]) + (0.587 * data[index + 1]) + (0.114 * data[index + 2])
    const boosted = Math.max(0, Math.min(255, (grayscale - 128) * 1.4 + 128))
    const value = boosted > 165 ? 255 : 0

    data[index] = value
    data[index + 1] = value
    data[index + 2] = value
    data[index + 3] = 255
  }

  context.putImageData(imageData, 0, 0)
  return canvas
}

async function renderPdfPageToCanvas(page: any): Promise<HTMLCanvasElement> {
  const viewport = page.getViewport({ scale: OCR_RENDER_SCALE })
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d', { alpha: false })

  if (!context) {
    throw new Error('Impossible de preparer un canvas OCR')
  }

  canvas.width = Math.ceil(viewport.width)
  canvas.height = Math.ceil(viewport.height)

  await page.render({
    canvasContext: context,
    viewport
  }).promise

  return prepareCanvasForOcr(canvas)
}

async function extractTextWithOcr(pdfDocument: PdfDocument): Promise<string> {
  return queueOcrTask(async () => {
    const worker = await getOcrWorker()
    const pagesToRead = Math.min(pdfDocument.numPages, MAX_OCR_PAGES)
    const pageTexts: string[] = []

    for (let pageNumber = 1; pageNumber <= pagesToRead; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber)
      const canvas = await renderPdfPageToCanvas(page)
      const result = await worker.recognize(canvas)
      const ocrText = normalizePdfText(result.data.text || '')

      if (ocrText) {
        pageTexts.push(`Page ${pageNumber} OCR: ${ocrText}`)
      }
    }

    return pageTexts.join('\n\n').slice(0, MAX_TEXT_LENGTH)
  })
}

async function extractTextFromPdf(fileUrl: string): Promise<string> {
  const pdfDocument = await loadPdfDocument(fileUrl)

  try {
    const embeddedText = await extractEmbeddedTextFromPdf(pdfDocument)

    if (!shouldRunOcr(embeddedText)) {
      return embeddedText
    }

    try {
      const ocrText = await extractTextWithOcr(pdfDocument)
      return [embeddedText, ocrText]
        .filter((value) => value.trim().length > 0)
        .join('\n\n')
        .slice(0, MAX_TEXT_LENGTH)
    } catch (ocrError) {
      console.warn('OCR fallback indisponible pour ce PDF:', ocrError)
      return embeddedText
    }
  } finally {
    await pdfDocument.destroy()
  }
}

function buildFallbackAnalysis(fileName: string, textContent: string, errorMessage?: string): DocumentAnalysisResult {
  const lowerName = fileName.toLowerCase()
  const fallbackType = normalizeDocumentType(null, fileName)
  const fallbackDate =
    normalizeOptionalMedicalDate(textContent.match(/\b(20\d{2}-\d{2}-\d{2})\b/)?.[1]) ||
    extractDateFromFileName(fileName) ||
    new Date().toISOString().split('T')[0]
  const fallbackPrelevement = fallbackType === 'blood_test' ? extractDateFromFileName(fileName) || fallbackDate : null

  return {
    type_document: fallbackType,
    date_document: fallbackDate,
    medecin: null,
    laboratoire: null,
    date_prelevement: fallbackPrelevement,
    prescriptions: [],
    analyses: [],
    diagnostics: [],
    recommandations: errorMessage
      ? `Analyse partielle. Cause: ${errorMessage}`
      : 'Analyse partielle, verification manuelle recommandee.',
    resume: `Document ${lowerName.includes('analyse') ? 'd analyse' : 'medical'} importe.`
  }
}

function buildJsonRepairPrompt(fileName: string, rawResponse: string): string {
  return `
La reponse suivante devait etre un JSON medical structure, mais elle est invalide.

Repare-la pour obtenir un JSON STRICTEMENT valide en conservant uniquement les informations deja presentes.
- N invente aucune nouvelle donnee
- Respecte la structure attendue
- Retourne uniquement le JSON final
- Utilise un JSON minifie sur une seule ligne

Nom du fichier: ${fileName}

Reponse a reparer:
${rawResponse}
`.trim()
}

async function parseClaudeAnalysisResponse(response: string, fileName: string): Promise<DocumentAnalysisResult> {
  try {
    return normalizeAnalysisResult(JSON.parse(sanitizeClaudeJsonResponse(response)), fileName)
  } catch {
    const repairedResponse = await callClaude(
      buildJsonRepairPrompt(fileName, response),
      'Tu repars des donnees deja extraites et tu retournes uniquement un JSON valide.',
      3000
    )

    return normalizeAnalysisResult(JSON.parse(sanitizeClaudeJsonResponse(repairedResponse)), fileName)
  }
}

export async function analyzeDocumentWithClaude(
  fileUrl: string,
  fileName: string
): Promise<DocumentAnalysisResult> {
  let pdfText = ''

  try {
    pdfText = await extractTextFromPdf(fileUrl)

    const prompt = `
Analyse ce document medical en francais a partir du texte extrait ci-dessous.

Objectif:
- identifier le type de document
- extraire le medecin prescripteur ou signataire
- extraire la date du document
- extraire la date de prelevement si presente
- extraire toutes les analyses biologiques et leurs resultats
- extraire les prescriptions si presentes
- extraire les diagnostics si presents

Reponds STRICTEMENT en JSON avec cette structure:
{
  "type_document": "blood_test|radiology|prescription|consultation_report|discharge_summary|pathology|cardiology|specialist_report|vaccination_record|insurance_document|other",
  "date_document": "YYYY-MM-DD",
  "date_prelevement": "YYYY-MM-DD ou null",
  "medecin": "string ou null",
  "laboratoire": "string ou null",
  "prescriptions": [
    {
      "medicament": "string",
      "dosage": "string ou null",
      "frequence": "string ou null",
      "duree": "string ou null",
      "instructions": "string ou null"
    }
  ],
  "analyses": [
    {
      "nom_analyse": "string",
      "valeur": "string ou null",
      "unite": "string ou null",
      "valeur_reference": "string ou null",
      "statut": "normal|anormal|limite|critique ou null"
    }
  ],
  "diagnostics": [
    {
      "diagnostic": "string",
      "type_diagnostic": "principal|secondaire|suspect|elimine",
      "gravite": "leger|modere|severe|critique ou null"
    }
  ],
  "recommandations": "string ou null",
  "resume": "resume court en 2 ou 3 phrases"
}

Regles:
- n invente pas d information absente
- si une section est absente, retourne []
- si une valeur est absente, retourne null
- conserve les noms exacts des analyses et les valeurs telles qu ecrites
- pour un bilan biologique, capture chaque ligne d analyse exploitable
- si le nom du fichier contient une date au format jj.mm.aa, jj-mm-aa ou jj/mm/aa, utilise-la comme secours fiable
- pour un bilan sanguin, privilegie la date de prelevement medicale plutot que la date d import
- si le document contient des analyses biologiques exploitables, type_document doit etre blood_test
- retourne un JSON minifie sur une seule ligne, sans markdown ni commentaires

Nom du fichier: ${fileName}

Texte extrait:
${pdfText || '[aucun texte extrait]'}
`.trim()

    const response = await callClaude(
      prompt,
      'Tu es un assistant specialise dans l extraction de donnees medicales structurees depuis des documents PDF.',
      3000
    )

    const initialAnalysis = await parseClaudeAnalysisResponse(response, fileName)
    return await verifyBloodTestCompleteness(fileName, pdfText, initialAnalysis)
  } catch (error) {
    console.error('Erreur analyse Claude:', error)
    return buildFallbackAnalysis(
      fileName,
      pdfText,
      error instanceof Error ? error.message : 'Erreur inconnue'
    )
  }
}

export async function analyzeLocalMedicalDocument(file: File): Promise<DocumentAnalysisResult> {
  const objectUrl = URL.createObjectURL(file)

  try {
    return await analyzeDocumentWithClaude(objectUrl, file.name)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
